/**
 * おすすめ求人（Workgate + Carat 混在）の状態と操作。
 *
 * MyPage / WorkgateDialog の2画面で使うため composable に置く
 * （CLAUDE.md: Composition の置き場 3 — 複数 SFC で再利用）。
 * 通信は services/recommend-jobs-client（JSON）に委ね、ここでは状態と
 * 提供元ごとの分岐だけを扱う。トースト表示は呼び出し側 SFC の責務。
 */
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import {
  applyCaratJob as postCaratApply,
  applyWorkgateJob as postWorkgateApply,
  fetchRecommendJobs,
  fetchWorkgateDetail,
  extractApplyError
} from '@/services/recommend-jobs-client'
import type {
  CaratApplyRequest,
  RecommendJobsRequest,
  WorkgateApplyRequest
} from '@/services/recommend-jobs-client'
import {
  toApplicantOccupation,
  toRecommendDetailShape,
  useRecommendJobBlocks
} from '@/composables/useRecommendJobBlocks'
import type {
  PositionedOffer,
  RecommendLayout,
  RecommendProviderGroup
} from '@/composables/useRecommendJobBlocks'

const PROVIDER_CARAT = 'carat'
const PROVIDER_WORKGATE = 'workgate'

/** Carat 応募に必要な、SFC 側から渡す文脈 */
export interface CaratApplyContext {
  uuid: string
  formatId: string
  deviceFlag: string
  appFlag: string
  siteFlag: string
}

/** 応募の結果。SFC 側がこれを見てトーストを出す */
export interface ApplyResult {
  ok: boolean
  /** テスト氏名のため提供元へ送信されなかった（DBにも残らない） */
  skippedAsTest?: boolean
  /** 提供元が応募済みと判定した。カードは応募済みへ落とす */
  alreadyApplied?: boolean
  /** 失敗時に画面へ出す文言。ok=false なら必ず入る */
  message?: string
  /**
   * 画面の通知種別。失敗の重さは呼び出し側ごとに判断させず、ここで確定させる。
   * 送信されていない（テスト氏名）・応募済みは異常ではないので warning。
   */
  messageType?: 'warning' | 'error'
}

/** 失敗結果を1か所で組み立て、文言と通知種別の対応を崩さない */
function applyFailure(
  message: string,
  extra: Partial<ApplyResult> = {}
): ApplyResult {
  const soft = extra.skippedAsTest || extra.alreadyApplied
  return {
    ok: false,
    message,
    messageType: soft ? 'warning' : 'error',
    ...extra
  }
}

export function useRecommendJobs(layout: RecommendLayout) {
  const providers: Ref<RecommendProviderGroup[]> = ref([])
  const loaded = ref(false)
  const keyword = ref('')
  /** 送信中の求人キー（`provider-job_id`）。同一求人の二重送信を止める */
  const applyingKey = ref('')

  const { blocks, blockSize, totalCount } = useRecommendJobBlocks(
    providers,
    layout
  )
  const hasOffers = computed(() => totalCount.value > 0)

  const offerKey = (offer: { provider?: string; job_id?: string }) =>
    `${offer.provider || ''}-${offer.job_id || ''}`

  const isApplying = (offer: { provider?: string; job_id?: string }) =>
    !!applyingKey.value && applyingKey.value === offerKey(offer)

  /** 取得。失敗しても throw せず空で確定させる（枠が空になるだけ） */
  async function load(params: RecommendJobsRequest): Promise<void> {
    try {
      const data = await fetchRecommendJobs(params)
      providers.value = data.providers
      keyword.value = data.keyword
    } catch (e) {
      providers.value = []
      keyword.value = ''
    } finally {
      loaded.value = true
    }
  }

  /**
   * 応募成功時に元データ側の is_applied を立てる。
   * ブロックの offer はコピーなので、ここを更新しないと表示が戻る。
   */
  function markApplied(offer: { provider?: string; job_id?: string }): void {
    for (const group of providers.value) {
      if (!group || group.provider !== offer.provider) continue
      for (const item of group.offers || []) {
        if (String(item.job_id) === String(offer.job_id)) {
          item.is_applied = true
          return
        }
      }
    }
  }

  async function applyCarat(
    offer: PositionedOffer,
    source: 'list' | 'detail',
    ctx: CaratApplyContext
  ): Promise<ApplyResult> {
    applyingKey.value = offerKey(offer)
    const payload: CaratApplyRequest = {
      uuid: ctx.uuid,
      job_id: String(offer.job_id),
      search_id: offer.search_id || '',
      format_id: ctx.formatId,
      // 応募者の区分。求人の雇用形態を送ると応募者情報が誤る
      occupation: toApplicantOccupation(ctx.formatId),
      detail_flag: source === 'detail' ? 'd' : '',
      keyword: keyword.value || '',
      area: offer.pref_name || '',
      display_position: offer.displayPosition || 0,
      block_index: offer.blockIndex || 0,
      device_flg: ctx.deviceFlag,
      app_flag: ctx.appFlag,
      site_flag: ctx.siteFlag
    }
    try {
      const res = await postCaratApply(payload)
      if (res.test === 'ok') {
        // 氏名・カナに「テスト」等が含まれる会員は提供元へ送信されない
        return applyFailure('テストデータのため送信されていません', {
          skippedAsTest: true
        })
      }
      if (res.result !== 'ok') {
        return applyFailure(res.message || '応募できませんでした')
      }
      markApplied(offer)
      return { ok: true }
    } catch (e) {
      // varuna は応募失敗を HTTP 400 で返すため、本文は catch 側で読む
      const err = extractApplyError(e)
      if (err.error_code === 'DUPLICATE_APPLICATION') {
        // 同じ会社に応募済み。再送しても必ず失敗するのでカードを応募済みにする
        markApplied(offer)
        return applyFailure('すでに応募済みの企業です', {
          alreadyApplied: true
        })
      }
      return applyFailure(err.message || '応募失敗しました')
    } finally {
      applyingKey.value = ''
    }
  }

  async function applyWorkgate(
    offer: PositionedOffer,
    payload: WorkgateApplyRequest
  ): Promise<ApplyResult> {
    applyingKey.value = offerKey(offer)
    try {
      await postWorkgateApply(payload)
      markApplied(offer)
      return { ok: true }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        markApplied(offer)
        return applyFailure('すでに応募済みです', { alreadyApplied: true })
      }
      return applyFailure('応募失敗しました')
    } finally {
      applyingKey.value = ''
    }
  }

  /**
   * 詳細データを組む。Workgate は詳細APIを引き、失敗したら一覧データで代替する。
   * Carat は一覧レスポンスに説明文まで含まれるためそれを整形して使う。
   */
  async function buildDetail(
    offer: PositionedOffer
  ): Promise<Record<string, unknown> | null> {
    if (offer.provider === PROVIDER_CARAT) {
      return toRecommendDetailShape(offer)
    }
    const detail = await fetchWorkgateDetail(String(offer.job_id))
    if (!detail) return toRecommendDetailShape(offer)
    // 詳細APIの生レスポンスには識別子と計測用の順位が無い。ここで引き継ぐ
    return {
      ...detail,
      provider: offer.provider || PROVIDER_WORKGATE,
      job_id: offer.job_id,
      displayPosition: offer.displayPosition || 0,
      blockIndex: offer.blockIndex || 0,
      apply_enabled: offer.apply_enabled !== false,
      is_applied: offer.is_applied === true
    }
  }

  return {
    providers,
    loaded,
    keyword,
    applyingKey,
    blocks,
    blockSize,
    totalCount,
    hasOffers,
    isApplying,
    load,
    markApplied,
    applyCarat,
    applyWorkgate,
    buildDetail
  }
}
